import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DoorOpen, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, humanise } from '@/lib/format';
import {
  Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, LoadingBlock, Modal,
  MoneyInput, PageHeader, Select, Table, TableWrap, Td, Textarea, Th, useToast, statusTone,
} from '@/components/ui';
import { COMMON_FACILITIES } from './Properties';
import type { Room, Property, RoomStatus } from '@shared/types';

const STATUSES: RoomStatus[] = ['vacant', 'occupied', 'maintenance', 'reserved', 'unavailable'];

export default function Rooms() {
  const { currency, can } = useAuth();
  const [propertyId, setPropertyId] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Room | null>(null);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data, loading, reload } = useApi<Room[]>('/rooms', {
    property_id: propertyId || undefined,
    status: status || undefined,
  });

  const rooms = data ?? [];
  const perRoomProperties = (properties ?? []).filter((p) => p.letting_mode === 'per_room');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rooms"
        subtitle={`${rooms.length} room${rooms.length === 1 ? '' : 's'} across the portfolio`}
        action={can('staff') && perRoomProperties.length > 0 && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Add room
          </Button>
        )}
      />

      <div className="flex flex-wrap gap-3">
        <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-auto min-w-[200px]">
          <option value="">All properties</option>
          {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[160px]">
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
        </Select>
      </div>

      <Card>
        {loading ? (
          <div className="p-5"><LoadingBlock rows={6} /></div>
        ) : rooms.length === 0 ? (
          <EmptyState
            icon={<DoorOpen className="h-6 w-6" />}
            title="No rooms found"
            message={
              perRoomProperties.length === 0
                ? 'Add a property that is let room by room, then add its rooms.'
                : 'Try clearing the filters, or add a room.'
            }
            action={can('staff') && perRoomProperties.length > 0 && (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>Add room</Button>
            )}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Room</Th>
                  <Th>Property</Th>
                  <Th>Type</Th>
                  <Th align="right">Rent</Th>
                  <Th align="right">Deposit</Th>
                  <Th>Tenant</Th>
                  <Th>Status</Th>
                  {can('staff') && <Th align="right">Edit</Th>}
                </tr>
              </thead>
              <tbody>
                {rooms.map((room) => (
                  <tr key={room.id} className="row-hover">
                    <Td className="font-semibold">{room.name}</Td>
                    <Td>
                      <Link to={`/app/properties/${room.property_id}`} className="text-muted hover:text-brand">
                        {room.property_name}
                      </Link>
                    </Td>
                    <Td className="text-muted">
                      {room.room_type ?? '—'}
                      {room.floor ? <span className="text-faint"> · Floor {room.floor}</span> : null}
                    </Td>
                    <Td align="right" className="font-semibold tnum whitespace-nowrap">
                      {money(room.rent_cents, currency)}
                    </Td>
                    <Td align="right" className="text-muted tnum whitespace-nowrap">
                      {money(room.deposit_cents, currency)}
                    </Td>
                    <Td>
                      {room.tenant_name ? (
                        <Link to={`/app/tenants/${room.tenant_id}`} className="link">{room.tenant_name}</Link>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </Td>
                    <Td><Badge tone={statusTone(room.status)} dot>{humanise(room.status)}</Badge></Td>
                    {can('staff') && (
                      <Td align="right">
                        <Button size="sm" variant="outline" onClick={() => setEditing(room)}>Edit</Button>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {creating && (
        <RoomFormModal
          properties={perRoomProperties}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); reload(); }}
        />
      )}

      {editing && (
        <RoomFormModal
          room={editing}
          propertyId={editing.property_id}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

export function RoomFormModal({ room, propertyId, properties, onClose, onDone }: {
  room?: Room;
  propertyId?: string;
  properties?: Property[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { currency, can } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState(propertyId ?? room?.property_id ?? '');
  const [form, setForm] = useState({
    name: room?.name ?? '',
    room_type: room?.room_type ?? '',
    floor: room?.floor ?? '',
    max_occupants: room?.max_occupants ?? 1,
    status: room?.status ?? ('vacant' as RoomStatus),
    notes: room?.notes ?? '',
  });
  const [rentCents, setRentCents] = useState(room?.rent_cents ?? 0);
  const [depositCents, setDepositCents] = useState(room?.deposit_cents ?? 0);
  const [facilities, setFacilities] = useState<string[]>(room?.facilities ?? []);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const ROOM_FACILITIES = [
    'Ensuite bathroom', 'Shared bathroom', 'Balcony', 'Built-in wardrobe', 'Air conditioning',
    'Ceiling fan', 'Furnished', 'Kitchenette', 'Desk', 'Window view', ...COMMON_FACILITIES.slice(0, 4),
  ];

  async function submit() {
    if (!form.name.trim()) { toast.error('Give the room a name or number'); return; }
    if (!room && !selectedProperty) { toast.error('Choose a property'); return; }

    setBusy(true);
    try {
      const payload = {
        ...form,
        property_id: selectedProperty,
        max_occupants: Number(form.max_occupants) || 1,
        rent_cents: rentCents,
        deposit_cents: depositCents,
        facilities,
      };
      if (room) await api.patch(`/rooms/${room.id}`, payload);
      else await api.post('/rooms', payload);
      toast.success(room ? 'Room updated' : 'Room added');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the room');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.del(`/rooms/${room!.id}`);
      toast.success('Room deleted');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the room');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={room ? `Edit ${room.name}` : 'Add a room'}
        footer={
          <>
            {room && can('manager') && (
              <Button variant="ghost" onClick={() => setDeleting(true)} disabled={busy}
                icon={<Trash2 className="h-4 w-4" />}>
                Delete
              </Button>
            )}
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
          </>
        }
      >
        <div className="space-y-4">
          {!room && properties && (
            <Field label="Property" required>
              <Select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)}>
                <option value="">Choose a property…</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
          )}

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Name / number" required>
              <Input value={form.name} onChange={set('name')} placeholder="A1" autoFocus />
            </Field>
            <Field label="Type">
              <Input value={form.room_type} onChange={set('room_type')} placeholder="Ensuite double" />
            </Field>
            <Field label="Floor">
              <Input value={form.floor} onChange={set('floor')} placeholder="1" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Monthly rent" required>
              <MoneyInput valueCents={rentCents} onChangeCents={setRentCents} currency={currency} />
            </Field>
            <Field label="Deposit">
              <MoneyInput valueCents={depositCents} onChangeCents={setDepositCents} currency={currency} />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Maximum occupants">
              <Input
                type="number"
                min={1}
                value={form.max_occupants}
                onChange={(e) => setForm((f) => ({ ...f, max_occupants: Number(e.target.value) }))}
              />
            </Field>
            <Field
              label="Status"
              hint={room?.status === 'occupied'
                ? 'Occupied rooms follow their lease — end the lease to free it'
                : 'Set by assigning a tenant'}
            >
              <Select
                value={form.status}
                onChange={set('status')}
                disabled={room?.status === 'occupied'}
              >
                {STATUSES.filter((s) => s !== 'occupied' || room?.status === 'occupied')
                  .map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
              </Select>
            </Field>
          </div>

          <div>
            <p className="label mb-2">Facilities</p>
            <div className="flex flex-wrap gap-2">
              {[...new Set([...ROOM_FACILITIES, ...facilities])].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFacilities((list) =>
                    list.includes(f) ? list.filter((x) => x !== f) : [...list, f])}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    facilities.includes(f)
                      ? 'bg-brand/15 text-brand border-brand/30'
                      : 'bg-raised text-muted border-line hover:text-ink'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <Field label="Notes">
            <Textarea value={form.notes} onChange={set('notes')} />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => void remove()}
        loading={busy}
        title="Delete this room?"
        confirmLabel="Delete"
        message="This cannot be undone. Rooms with lease history cannot be deleted — set them to unavailable instead."
      />
    </>
  );
}
